import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  AccountAdditionalInfoPageFlatFields,
  AccountBinInputPageCvcFlatFields,
  AccountBinInputPageFlatFields,
  AccountDisplayNameFlatFields,
  amexCardTypes,
  PaymentNetInputPageFlatFields,
  RegisterAccountCountryFields,
} from 'src/app/RegisterAccount/RegisterAccount'
import AppName from 'src/components/AppName/AppName'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import RegisterAccountCardInfo from 'src/components/RegisterAccountCardInfo/RegisterAccountCardInfo'
import RegisterAccountCardInfoJPM from 'src/components/RegisterAccountCardInfoJPM/RegisterAccountCardInfoJPM'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { IssuerSchemaFilterFields, SupportedBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { CountryListModel } from 'src/models/country.models'
import {
  IssuerIntegratorListModel,
  IssuerIntegratorSchemaFieldListModel,
} from 'src/models/issuerIntegrator.models'
import { registerAccountUrl } from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { industryOptionsFromConfig } from 'src/queries/session/config/config.options'
import { sessionConfigQueries } from 'src/queries/session/config/config.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectBrandName } from 'src/redux/selectors/session.selectors'
import styled, { css } from 'styled-components'

const ValuesDiv = styled.div`
  margin-top: 10px;
  padding: 40px;
  background-color: ${styleConstants.colors.gray_lightest};
  border: 1px solid ${styleConstants.colors.gray_light};
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
`
const ValuesLeftDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`

const ValuesSectionDiv = styled.div`
  display: flex;
  flex-direction: column;

  &:nth-child(n + 2) {
    margin-top: 40px;
  }
`

const ValuesTitleSpan = styled.span`
  ${getFontCss(
    20,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )};
`

const ValuesStackDiv = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
`
const ValuesValueSpan = styled.span`
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  line-height: 20px;
`

const ValuesLabelSpan = styled.span`
  ${getFontCss(
    14,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
  margin-bottom: 8px;
  line-height: 18px;
`

interface RegisterAccountReviewInformationProps {
  className?: string
  displayNameFlatFields: AccountDisplayNameFlatFields
  accountBinInputPageFlatFields: AccountBinInputPageFlatFields
  accountBinInputPageCvcFlatFields: AccountBinInputPageCvcFlatFields
  paymentNetInputPageFlatFields?: PaymentNetInputPageFlatFields
  selectedIssuer?: IssuerIntegratorListModel
  issuerSchema?: IssuerIntegratorSchemaFieldListModel[]
  issuerFields: Record<string, string>
  selectedCountry?: CountryListModel
  countryFields: RegisterAccountCountryFields
  accountAdditionalInfoPageFlatFields: AccountAdditionalInfoPageFlatFields
  returnToDisplayName: () => void
  returnToCardInformation: () => void
  returnToAdditionalInformation?: () => void
  postingCreditCard: boolean
  topLevelError?: string
  onSubmit: () => void
  hasOrganization: boolean
  extraPadding?: boolean
}

const RegisterAccountReviewInformation: React.FC<
  RegisterAccountReviewInformationProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  // Default [] preserves the legacy redux initial-state render (empty option
  // list while pending) — the config reducer had no loading/error flags.
  const { data: supportIndustriesLocalized = [] } = useQuery({
    ...sessionConfigQueries.byLocale(locale),
    select: industryOptionsFromConfig,
  })
  const { analytics } = useAnalyticsContext()
  const [acknowledgedAccess, setAcknowledgedAccess] = useState(false)
  const orgId = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )?.id
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({ orgId })
  const brand = useSelector(selectBrandName)
  const isJpm = brand === SupportedBrands.Jpm

  const needsCardholderVerification =
    props.selectedIssuer?.needsCardholderVerification
  const submitText = needsCardholderVerification
    ? translate('Confirm and Send Verification Code')
    : translate('Confirm Account Registration')

  const filteredIssuerSchemaFields =
    props.issuerSchema?.filter(
      (field) =>
        field.name !== IssuerSchemaFilterFields.cardNumber &&
        field.name !== IssuerSchemaFilterFields.expires &&
        field.name !== IssuerSchemaFilterFields.cvc,
    ) || []

  const cardType = amexCardTypes.filter(
    (cardType) =>
      cardType.value === props.accountAdditionalInfoPageFlatFields?.cardType,
  )[0]
  const provinceLabel =
    props.selectedCountry?.fields.find((field) => field.name === 'province')
      ?.label ?? translate('State')
  const postalLabel =
    props.selectedCountry?.fields.find((field) => field.name === 'postal')
      ?.label ?? translate('ZIP Code')

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        ${props.extraPadding &&
        css`
          padding: 60px;
        `}
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          ${getFontCss(
            32,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          margin-bottom: 30px;
          width: 80%;
        `}
      >
        {translate('Almost done! Please review and confirm registration')}
      </div>
      {isJpm ? (
        <RegisterAccountCardInfoJPM
          selectedIssuer={props.selectedIssuer}
          paymentNetInputPageFlatFields={props.paymentNetInputPageFlatFields}
          returnToCardInformation={props.returnToCardInformation}
        />
      ) : (
        <RegisterAccountCardInfo
          selectedIssuer={props.selectedIssuer}
          accountBinInputPageFlatFields={props.accountBinInputPageFlatFields}
          accountBinInputPageCvcFlatFields={
            props.accountBinInputPageCvcFlatFields
          }
          returnToCardInformation={props.returnToCardInformation}
        />
      )}
      <ValuesDiv>
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <ValuesSectionDiv>
            {cardType && (
              <>
                <ValuesTitleSpan>{translate('Card Type')}</ValuesTitleSpan>
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )};
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                    margin-top: 8px;
                    margin-bottom: 20px;
                  `}
                >
                  {cardType.fieldText}
                </span>
              </>
            )}
            <ValuesTitleSpan>{translate('Billing Info')}</ValuesTitleSpan>
            <ValuesStackDiv>
              {props.selectedCountry !== undefined && (
                <ValuesLeftDiv>
                  <ValuesLabelSpan>{translate('Country')}</ValuesLabelSpan>
                  <ValuesValueSpan>
                    {translate('${country}', {
                      country: props.selectedCountry.label,
                    })}
                  </ValuesValueSpan>
                </ValuesLeftDiv>
              )}
              {props.countryFields.address1 !== undefined && (
                <ValuesLeftDiv>
                  <ValuesLabelSpan>
                    {translate('Billing Address 1')}
                  </ValuesLabelSpan>
                  <ValuesValueSpan>
                    {translate('${address1}', {
                      address1: props.countryFields.address1,
                    })}
                  </ValuesValueSpan>
                </ValuesLeftDiv>
              )}
              {props.countryFields.address2 !== undefined && (
                <ValuesLeftDiv>
                  <ValuesLabelSpan>
                    {translate('Billing Address 2')}
                  </ValuesLabelSpan>
                  <ValuesValueSpan>
                    {translate('${address2}', {
                      address2: props.countryFields.address2,
                    })}
                  </ValuesValueSpan>
                </ValuesLeftDiv>
              )}
              {props.countryFields.city !== undefined && (
                <ValuesLeftDiv>
                  <ValuesLabelSpan>{translate('City')}</ValuesLabelSpan>
                  <ValuesValueSpan>
                    {translate('${city}', {
                      city: props.countryFields.city,
                    })}
                  </ValuesValueSpan>
                </ValuesLeftDiv>
              )}
              {(props.countryFields.province || props.countryFields.postal) && (
                <div
                  css={css`
                    display: flex;
                    flex-direction: row;
                  `}
                >
                  {props.countryFields.province && (
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                      `}
                    >
                      <ValuesLabelSpan>{provinceLabel}</ValuesLabelSpan>
                      <ValuesValueSpan>
                        {translate('${province}', {
                          province: props.countryFields.province,
                        })}
                      </ValuesValueSpan>
                    </div>
                  )}
                  {props.countryFields.postal && (
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        ${props.countryFields.province && `margin-left: 40px;`}
                      `}
                    >
                      <ValuesLabelSpan>{postalLabel}</ValuesLabelSpan>
                      <ValuesValueSpan>
                        {translate('${postal}', {
                          postal: props.countryFields.postal,
                        })}
                      </ValuesValueSpan>
                    </div>
                  )}
                </div>
              )}
            </ValuesStackDiv>
          </ValuesSectionDiv>
          {filteredIssuerSchemaFields.length > 0 &&
            brand !== SupportedBrands.Jpm && (
              <ValuesSectionDiv>
                <ValuesTitleSpan>
                  {translate('Additional Info')}
                </ValuesTitleSpan>
                {filteredIssuerSchemaFields.map((field, i) => (
                  <ValuesStackDiv key={i}>
                    <FieldLabel>{field.label}</FieldLabel>
                    <ValuesValueSpan>
                      {props.issuerFields[field.name]}
                    </ValuesValueSpan>
                  </ValuesStackDiv>
                ))}
              </ValuesSectionDiv>
            )}
          {!props.hasOrganization && (
            <ValuesSectionDiv>
              <ValuesTitleSpan>
                {translate('Company Information')}
              </ValuesTitleSpan>
              <ValuesStackDiv>
                <FieldLabel>{translate('Company Name')}</FieldLabel>
                <ValuesValueSpan>
                  {props.accountAdditionalInfoPageFlatFields.companyName}
                </ValuesValueSpan>
              </ValuesStackDiv>
              <ValuesStackDiv>
                <FieldLabel>{translate('Industry')}</FieldLabel>
                <ValuesValueSpan>
                  {
                    Object.values(
                      supportIndustriesLocalized.filter(
                        (industry) =>
                          industry.value ===
                          props.accountAdditionalInfoPageFlatFields.industry,
                      ),
                    ).map((industry) =>
                      industry.details ? industry.details.text : '',
                    )[0]
                  }
                </ValuesValueSpan>
              </ValuesStackDiv>
            </ValuesSectionDiv>
          )}
        </div>
        <div>
          <HoverableLink
            css={css`
              display: flex;
              align-items: center;
            `}
            onClick={props.returnToAdditionalInformation}
            ariaLabelSource={'Update'}
            testId="updateAddress"
          >
            <SvgUpdate
              css={css`
                transform: scale(calc(14 / 24));
              `}
            />
            {translate('Update')}
          </HoverableLink>
        </div>
      </ValuesDiv>
      <ValuesDiv>
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <ValuesSectionDiv>
            <ValuesTitleSpan>
              {translate('Account Display Name')}
            </ValuesTitleSpan>
            <ValuesStackDiv>
              <ValuesValueSpan>
                {props.displayNameFlatFields.displayName}
              </ValuesValueSpan>
            </ValuesStackDiv>
          </ValuesSectionDiv>
        </div>
        <div>
          <HoverableLink
            css={css`
              display: flex;
              align-items: center;
            `}
            onClick={(): void => {
              analytics?.track(
                'register account - review - update account name',
                {
                  intent: 'edit account name',
                  interaction: 'button press',
                  screen: registerAccountUrl(),
                },
              )
              props.returnToDisplayName()
            }}
            ariaLabelSource={'Update'}
            testId="updateAccountName"
          >
            <SvgUpdate
              css={css`
                transform: scale(calc(14 / 24));
              `}
            />
            {translate('Update')}
          </HoverableLink>
        </div>
      </ValuesDiv>
      {/* acknowledgements go here */}
      {orgFeatureFlags?.ownerAdminEnhancementsEnabled ? (
        <div
          data-testid="rcnAcknolwedgement"
          css={css`
            display: flex;
            flex-direction: column;
            margin-top: 40px;
            gap: 12px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )}
            `}
          >
            {translate(
              orgFeatureFlags?.cardManagerRoleEnabled
                ? 'The Owner, any current or future Admin(s), and any other Card Managers assigned by the Owner will have the following edit access over your registered card account:'
                : 'The Owner and any current or future Admin(s) will have the following edit access over your registered card account:',
            )}
          </span>
          <ul
            css={css`
              list-style-position: inside;
            `}
          >
            <li>
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {translate(
                  'They can ${bold} virtual cards and budgets from your account',
                  {
                    bold: (
                      <span
                        css={css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )}
                        `}
                      >
                        {translate('create')}
                      </span>
                    ),
                  },
                )}
              </span>
            </li>
            <li>
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {translate(
                  'They can ${bold} virtual cards and budgets under your account',
                  {
                    bold: (
                      <span
                        css={css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )}
                        `}
                      >
                        {translate('close')}
                      </span>
                    ),
                  },
                )}
              </span>
            </li>
            <li>
              <span
                css={css`
                  ${getFontCss(
                    16,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.regular,
                  )}
                `}
              >
                {translate(
                  'They can ${bold} virtual card and budget details under your account',
                  {
                    bold: (
                      <span
                        css={css`
                          ${getFontCss(
                            16,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )}
                        `}
                      >
                        {translate('update')}
                      </span>
                    ),
                  },
                )}
              </span>
            </li>
          </ul>
          <span
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate(
              "All actions will be logged in the 'History' of each virtual card or budget, including details of the user who performed the action.",
            )}
          </span>
          <div
            css={css`
              display: flex;
              flex-direction: row;
            `}
          >
            <Checkbox
              isSelected={acknowledgedAccess}
              onClick={(): void => setAcknowledgedAccess(!acknowledgedAccess)}
              testId="editAccessAcknowledgeCheckbox"
            />
            {translate('I acknowledge this access')}
          </div>
        </div>
      ) : (
        needsCardholderVerification && (
          <div
            css={css`
              padding: 40px 20px;
            `}
          >
            <h3
              css={css`
                margin-bottom: 10px;
                ${getFontCss(
                  16,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('Verification Required')}
            </h3>
            <p
              css={css`
                margin-bottom: 10px;
                ${getFontCss(
                  12,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate(
                'After you confirm registration, a temporary charge containing a code will appear in your recent authorization activity with EX in the name or description.',
              )}
            </p>
            <ol
              css={css`
                padding-left: 15px;
              `}
            >
              <li
                css={css`
                  margin-bottom: 10px;
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate(
                  'Find the code in your recent authorization activity.',
                )}
              </li>
              <li
                css={css`
                  margin-bottom: 10px;
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('Enter that 5-letter code into ${appName}.', {
                  appName: <AppName />,
                })}
              </li>
            </ol>
          </div>
        )
      )}
      {props.topLevelError !== undefined && (
        <span
          css={css`
            margin-top: 40px;
            ${getFontCss(
              14,
              styleConstants.colors.red,
              styleConstants.fontFamilies.regular,
            )};

            ${getBreakpointDown(breakpointSizes.XSM)(css`
              margin-top: 30px;
            `)}
          `}
        >
          {props.topLevelError}
        </span>
      )}
      <div
        css={css`
          margin-top: 40px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
        `}
      >
        <HoverableLink
          css={css`
            cursor: pointer;
            display: flex;
            align-items: center;
          `}
          onClick={(): void => {
            analytics?.track('register account - review - back', {
              intent: 'go back to account name',
              interaction: 'button press',
              screen: registerAccountUrl(),
            })
            props.returnToDisplayName()
          }}
          ariaLabelSource={'Back to Account Name'}
        >
          <SvgChevronLeft
            css={css`
              transform: scale(calc(14 / 24));
            `}
          />
          {translate('Back to Account Name')}
        </HoverableLink>
        <Button
          id={'completeRegButton'}
          testId="confirm-button"
          onClick={props.onSubmit}
          type={ButtonTypes.button}
          design={ButtonDesigns.designCta}
          text={submitText}
          loading={props.postingCreditCard}
          disabled={
            props.postingCreditCard ||
            (!acknowledgedAccess &&
              orgFeatureFlags?.ownerAdminEnhancementsEnabled)
          }
          big
        />
      </div>
      {orgFeatureFlags?.ownerAdminEnhancementsEnabled &&
        needsCardholderVerification && (
          <div
            css={css`
              padding: 40px 20px;
            `}
          >
            <h3
              css={css`
                margin-bottom: 10px;
                ${getFontCss(
                  16,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('Verification Required')}
            </h3>
            <p
              css={css`
                margin-bottom: 10px;
                ${getFontCss(
                  12,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate(
                'After you confirm registration, a temporary charge containing a code will appear in your recent authorization activity with EX in the name or description.',
              )}
            </p>
            <ol
              css={css`
                padding-left: 15px;
              `}
            >
              <li
                css={css`
                  margin-bottom: 10px;
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate(
                  'Find the code in your recent authorization activity.',
                )}
              </li>
              <li
                css={css`
                  margin-bottom: 10px;
                  ${getFontCss(
                    12,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                {translate('Enter that 5-letter code into ${appName}.', {
                  appName: <AppName />,
                })}
              </li>
            </ol>
          </div>
        )}
    </div>
  )
}

export default RegisterAccountReviewInformation
